/**
 * Which local calendar day a health reading belongs to.
 *
 * `daily_aggregates[].date` is a LOCAL calendar day. Every reader depends on
 * that: `computeHealthTrend` windows on local days, the wellness rollup matches
 * the key against the recipient's local date, and `buildHealthPreview` drops
 * today's steps unless the key names today locally.
 *
 * For a long time no WRITER produced one, because both device paths went
 * through `.toISOString().slice(0, 10)`:
 *
 *   iOS      HKStatisticsCollectionQuery buckets are anchored at local
 *            midnight, so the UTC slice named the day BEFORE for every
 *            positive offset — steps blank all day east of UTC.
 *   Android   Health Connect hands over real record instants, so the UTC slice
 *            split ONE local day across two keys, under-reporting every
 *            cumulative total and, west of UTC, dating the evening's records
 *            tomorrow.
 *
 * `localDayKey` used to live privately in chatStartCards, where only the
 * readers could reach it. It lives here now so the writer uses the identical
 * function — the lesson of #2577, where a test fixture and its reader disagreed
 * about which day a bucket belonged to and the mismatch only showed after
 * 20:00. A convention with two implementations has already drifted. (ED-2699)
 */

/** yyyy-mm-dd for an epoch-ms instant, in the device's local timezone. */
export function localDayKey(value: number): string {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The instants a local calendar day starts and ends.
 *
 * For the paths that aggregate on device and so have no single sample to point
 * at. This replaces `${day}T00:00:00.000Z`, which named UTC midnight of a
 * LOCAL day — an instant up to a day away from the day it claimed. `startDate`
 * is still read as a real moment downstream (reading freshness, dedupe), so it
 * has to be one; the day key now carries the calendar day on its own.
 *
 * Built with the local Date constructor rather than by string arithmetic, so a
 * day that is 23 or 25 hours long across a DST transition still starts and ends
 * where the wearer's clock says it does.
 */
export function localDayBounds(dayKey: string): { startDate: string; endDate: string } {
  const [y, m, d] = dayKey.split('-').map(Number);
  return {
    startDate: new Date(y, m - 1, d, 0, 0, 0, 0).toISOString(),
    endDate: new Date(y, m - 1, d, 23, 59, 59, 0).toISOString(),
  };
}
